import {
  ActivityIcon,
  CrownIcon,
  ExploreIcon,
  LungsIcon,
  MeditationIcon,
  MoonIcon,
  ReadinessIcon,
  RingBatteryIcon,
  TypographyV3,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';

import ColorBlock from '../ColorBlock';
import Heading from '../Heading';
import IconRow from '../IconRow';
import P from '../P';
import ResponsiveImage from '../ResponsiveImage';
import Section from '../Section';
import getValues from '../utils/getValues';

const values = getValues({
  li: { variant: 'h6', className: 'my-6' },
});

const IpBrandFeatures = (): JSX.Element => {
  const t = useTranslations();

  return (
    <>
      <Heading tx="ip_brand_features_heading" />

      <P tx="ip_brand_features_content" />

      <Section title="ip_brand_trademarks_title">
        <ul>{t.rich('ip_brand_trademarks_list_new', values)}</ul>
      </Section>

      <Section title="ip_brand_slogans_title">
        <ul>{t.rich('ip_brand_slogans_list', values)}</ul>
      </Section>

      <Section title="ip_brand_ring_title">
        <P tx="ip_brand_ring_content" />

        <ResponsiveImage
          src="/intellectual-property-notice/features"
          width={2040}
          height={3090}
          alt="ip_brand_ring_features_alt"
        />
      </Section>

      <Section title="ip_brand_app_title">
        <P tx="ip_brand_app_content" />

        <ResponsiveImage
          src={t('ip_app_ui')}
          width={2040}
          height={1000}
          alt="ip_brand_app_alt"
        />

        <P tx="ip_brand_app_icons" />

        <table className="my-12">
          <thead>
            <tr>
              <th className="border px-8 py-6 text-left font-normal">
                {t('ip_brand_app_icon_col_icons')}
              </th>
              <th className="border px-8 py-6 text-left font-normal">
                {t('ip_brand_app_icon_col_use')}
              </th>
            </tr>
          </thead>

          <tbody>
            <IconRow icon={CrownIcon} tx="ip_brand_app_icon_crown" />
            <IconRow icon={ReadinessIcon} tx="ip_brand_app_icon_readiness" />
            <IconRow icon={MoonIcon} tx="ip_brand_app_icon_moon" />
            <IconRow icon={ActivityIcon} tx="ip_brand_app_icon_activity" />
            <IconRow icon={RingBatteryIcon} tx="ip_brand_app_icon_battery" />
            <IconRow icon={ExploreIcon} tx="ip_brand_app_icon_explore" />
            <IconRow icon={MeditationIcon} tx="ip_brand_app_icon_meditation" />
            <IconRow icon={LungsIcon} tx="ip_brand_app_icon_lungs" />
          </tbody>
        </table>
      </Section>

      <Section title="ip_brand_web_title">
        <P tx="ip_brand_web_content" />

        <ResponsiveImage
          src="/intellectual-property-notice/on-the-web"
          width={2094}
          height={2566}
          alt="ip_brand_web_content_alt"
        />
      </Section>

      <Section title="ip_brand_assets_title">
        <P tx="ip_brand_assets_content" />
      </Section>

      <Section title="ip_brand_themes_title">
        <P tx="ip_brand_themes_content" className="mb-12" />

        <section className="mb-12">
          <TypographyV3 Element="h4" variant="h5">
            {t('ip_brand_themes_palette_primary')}
          </TypographyV3>

          <ol
            className={cx(
              'grid w-full lg:h-72',
              'grid-cols-2 lg:grid-cols-10',
              'grid-rows-4 lg:grid-rows-2',
            )}
            style={{ backgroundColor: '#2F4A73' }}
          >
            <ColorBlock
              color="light"
              backgroundColor="#2F4A73"
              tx="ip_brand_themes_palette_primary_blue"
              className={cx(
                'col-start-1 col-end-3 lg:col-end-5',
                'row-start-1 row-end-3 lg:row-end-3',
              )}
            />

            <ColorBlock
              backgroundColor="#E6DED3"
              tx="ip_brand_themes_palette_primary_sandstone"
              className={cx(
                'col-start-1 col-end-3 lg:col-start-5 lg:col-end-8',
                'row-start-3 row-end-4 lg:row-start-1 lg:row-end-3',
              )}
            />

            <ColorBlock
              color="light"
              backgroundColor="#151619"
              tx="ip_brand_themes_palette_primary_black"
              className={cx(
                'col-start-1 col-end-1 lg:col-start-8 lg:col-end-11',
                'row-start-4 row-end-5 lg:row-start-1 lg:row-end-2',
              )}
            />

            <ColorBlock
              backgroundColor="#FFF"
              tx="ip_brand_themes_palette_primary_white"
              className={cx(
                'col-start-2 col-end-3 lg:col-start-8 lg:col-end-11',
                'row-start-4 row-end-5 lg:row-start-2 lg:row-end-3',
                'border-b border-r border-gray-200',
              )}
            />
          </ol>
        </section>

        <section className="mb-12">
          <TypographyV3 Element="h4" variant="h5">
            {t('ip_brand_themes_palette_secondary')}
          </TypographyV3>

          <ol
            className={cx(
              'grid w-full lg:h-72',
              'grid-cols-1 lg:grid-cols-10',
              'grid-rows-4 lg:grid-rows-1',
            )}
            style={{ backgroundColor: '#A2D3E8' }}
          >
            <ColorBlock
              backgroundColor="#A2D3E8"
              tx="ip_brand_themes_palette_secondary_blue"
              className={cx(
                'col-start-1 lg:col-end-5',
                'row-start-1 row-end-3 lg:row-end-2',
              )}
            />

            <ColorBlock
              backgroundColor="#F6F3EF"
              tx="ip_brand_themes_palette_secondary_sandstone"
              className={cx(
                'lg:col-start-5 lg:col-end-8',
                'row-start-3 lg:row-start-1',
              )}
            />

            <ColorBlock
              backgroundColor="#FC6558"
              tx="ip_brand_themes_palette_secondary_coral"
              className={cx(
                'lg:col-start-8 lg:col-end-11',
                'row-start-4 lg:row-start-1',
              )}
            />
          </ol>
        </section>
      </Section>
    </>
  );
};

export default IpBrandFeatures;
